/**
 * Abandoned baskets — who left something behind, who came back.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, Mail, ShoppingBasket, Trash2 } from 'lucide-react';

const money = (v, c = 'USD') => {
  const symbols = { USD: '$', GBP: '£', EUR: '€', ZAR: 'R' };
  return symbols[c] ? `${symbols[c]}${Number(v || 0).toFixed(2)}`
                    : `${c} ${Number(v || 0).toFixed(2)}`;
};

const when = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
};

export const ShopBasketsTab = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['shop-admin-baskets'],
    queryFn: async () => (await api.get('/shop/admin/baskets')).data,
  });

  const sweep = useMutation({
    mutationFn: async () => (await api.post('/shop/admin/baskets/sweep')).data,
    onSuccess: (res) => {
      toast.success(`${res.first} first nudges, ${res.second} second nudges sent`);
      queryClient.invalidateQueries({ queryKey: ['shop-admin-baskets'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: async (token) => (await api.delete(`/shop/admin/baskets/${token}`)).data,
    onSuccess: () => {
      toast.success('Basket removed');
      queryClient.invalidateQueries({ queryKey: ['shop-admin-baskets'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  const currency = data?.currency || 'USD';

  return (
    <div className="space-y-5" data-testid="shop-baskets">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Left behind</CardTitle>
          <CardDescription>
            A basket is kept the moment a shopper types their email at checkout.
            One nudge after {data?.first_nudge_hours}h, a second with a 10% code
            after {data?.second_nudge_hours}h. The sweep runs hourly on its own.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[['Open baskets', data?.open_count ?? 0, 'baskets-stat-open'],
            ['Value sitting there', money(data?.open_value, currency), 'baskets-stat-value'],
            ['Nudged', data?.nudged_count ?? 0, 'baskets-stat-nudged'],
            ['Came back and ordered', data?.recovered_count ?? 0, 'baskets-stat-recovered']]
            .map(([label, value, testId]) => (
              <div key={label}>
                <p className="text-xs text-slate-500">{label}</p>
                <p className="mt-1.5 text-xl font-semibold" data-testid={testId}>{value}</p>
              </div>
            ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button size="sm" variant="outline" data-testid="baskets-sweep-btn"
                disabled={sweep.isPending} onClick={() => sweep.mutate()}>
          {sweep.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                           : <Mail className="h-3.5 w-3.5 mr-1.5" />}
          Send any nudges that are due
        </Button>
      </div>

      {(data?.baskets || []).length === 0 ? (
        <Card><CardContent className="py-14 text-center" data-testid="shop-baskets-empty">
          <ShoppingBasket className="h-8 w-8 mx-auto text-slate-300" />
          <p className="mt-4 text-sm text-slate-500">No baskets have been left behind.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {data.baskets.map((b) => (
            <Card key={b.token} data-testid={`shop-basket-${b.token}`}>
              <CardContent className="py-4 flex flex-wrap items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{b.name || b.email}</span>
                    <Badge variant="outline"
                           className={`text-[10px] ${b.status === 'ordered'
                             ? 'border-emerald-200 text-emerald-700'
                             : 'text-slate-500'}`}>
                      {b.status === 'ordered' ? `ordered ${b.order_number}` : 'still open'}
                    </Badge>
                    {(b.reminders || []).map((r) => (
                      <Badge key={r.stage} variant="secondary" className="text-[10px]">
                        nudge {r.stage}{r.sent ? '' : ' (not sent)'}
                      </Badge>
                    ))}
                    {b.recovery_code && (
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {b.recovery_code}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {b.email} · {(b.lines || []).length} items ·{' '}
                    {money(b.subtotal, b.currency)} · updated {when(b.updated_at)}
                  </p>
                </div>
                <Button size="sm" variant="ghost" data-testid={`basket-delete-${b.token}`}
                        onClick={() => remove.mutate(b.token)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopBasketsTab;
